import { useEffect, useLayoutEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

/**
 * Where a page starts when you arrive at it.
 *
 * `BrowserRouter` does nothing about scroll — it swaps the tree and leaves the
 * window exactly where it was. So scrolling down the landing page and then
 * opening the Library dropped you into the middle of the Library; scrolling a
 * long list and opening a document opened the document halfway down. Not a
 * quirk of one page: every navigation in the app behaved that way.
 *
 * The rule people actually expect, and the one browsers implement for ordinary
 * documents:
 *
 *   FOLLOWING A LINK   → the top. You have not read this page yet.
 *   BACK / FORWARD     → where you left off. You are returning to something.
 *   A `#hash`          → that element, whichever kind of navigation it was.
 *
 * That third case matters here because the landing page's nav is anchor-based,
 * and the chant reader deep-links verses as `#v-<id>`.
 *
 * Two implementation notes:
 *
 *   `history.scrollRestoration = 'manual'` — otherwise the browser also tries to
 *   restore, on its own schedule, and the two fight: it wins on reload, we win
 *   on soft navigation, and the result is inconsistent rather than either.
 *
 *   A restore is RETRIED across a few frames. On BACK the target page usually
 *   has to render (and often resolve a lazy chunk or a cached query) before it
 *   is tall enough to scroll to the saved offset, so a single `scrollTo` lands
 *   short and silently does nothing.
 */

const KEY_PREFIX = "vu:scroll:";

function save(key: string, y: number): void {
  try {
    sessionStorage.setItem(KEY_PREFIX + key, String(y));
  } catch {
    /* private mode — losing scroll memory is survivable */
  }
}

function load(key: string): number | null {
  try {
    const v = sessionStorage.getItem(KEY_PREFIX + key);
    return v === null ? null : Number(v);
  } catch {
    return null;
  }
}

export function ScrollManager() {
  const { pathname, hash, key } = useLocation();
  const navType = useNavigationType();
  const keyRef = useRef(key);
  keyRef.current = key;

  useEffect(() => {
    if ("scrollRestoration" in window.history) window.history.scrollRestoration = "manual";
  }, []);

  // Remember where this history entry was left. Passive + write-only, so it
  // costs nothing on the scroll path.
  useEffect(() => {
    const onScroll = () => save(keyRef.current, window.scrollY);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      save(keyRef.current, window.scrollY); // and once more on the way out
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  useLayoutEffect(() => {
    if (hash) {
      const el = document.getElementById(decodeURIComponent(hash.slice(1)));
      if (el) {
        el.scrollIntoView();
        return;
      }
    }

    if (navType !== "POP") {
      window.scrollTo(0, 0);
      return;
    }

    const target = load(key);
    if (target === null || target === 0) {
      window.scrollTo(0, 0);
      return;
    }
    let frames = 0;
    let raf = 0;
    const settle = () => {
      window.scrollTo(0, target);
      // Stop as soon as we get there, or after ~20 frames (~330 ms), whichever
      // comes first — a page that never grows tall enough should not spin.
      if (Math.abs(window.scrollY - target) > 2 && frames++ < 20) {
        raf = requestAnimationFrame(settle);
      }
    };
    raf = requestAnimationFrame(settle);
    return () => cancelAnimationFrame(raf);
  }, [pathname, hash, key, navType]);

  return null;
}
